import { memoizeObject } from "../utils/memoize";

type UpdateFunc = (run?: () => void) => void;

export type PropsWithUpdate<T> = T & { update: UpdateFunc };

/**
 * This creates a function returning a configuration object for "om-react" directives (see "defineReactWrapperDirective()")
 *
 * @param component Function implementing the component
 * @param props Function returning the current props
 * @returns Function which returns the configuration expected by the "om-react" directive
 */
export function memoizeReactConfig<T extends React.ComponentType<P>, P extends { update?: UpdateFunc }>(
    component: T,
    props: () => Omit<React.ComponentPropsWithoutRef<T>, "update">
) {
    let version = 1;

    const update = (run?: () => void) => {
        run?.();
        version++;
    };

    // We use "memoizeObject()" to make sure that each call will not return a new object,
    // but rather the same one as the last returned, unless the props have changed
    // This is to avoid triggering an infinte digest in Angular.
    //
    // We also add a function called "update()" to the props, which can be called by the React component to force its update
    // This is done by incremeting "version" above, thereby causing memoizeObject() to return a new object upon the next call
    //
    // The "update()" function accepts another arbitrary "run()" function, which is called by "update()" before incrementing its version
    // This is simply there to allow simpler updates from React, for instance as follows:
    //
    // <DatePicker date={task.date} setDate={d => update(() => task.date = d)} />
    //
    const func = memoizeObject(() => {
        return {
            component,
            version,
            props: {
                ...props(),
                update,
            },
        };
    });

    Object.assign(func, { update });

    return func;
}

/**
 * This implements a directive named "om-react", which can be used to include a React component
 * inside a template controller by Angular
 *
 * The basic usage is as follows:
 *
 * <div om-react="vm.functionReturningAConfigObject()"></div>
 *
 * Where "functionReturningAConfigObject()" should be a function created by calling "memoizeReactConfig()",
 * and stored on the parent controller's vm, for example as follows:
 *
 *  terramapOptions = react.memoizeReactConfig(TerramapOptions, () => ({
 *       task: this.task
 * }))
 *
 * The call above defines a config function which will:
 * - Use the "TerramapOptions" as the function component
 * - And pass an object
 */
export function defineReactWrapperDirective(): ng.IDirective {
    return {
        scope: {
            omReact: "<",
        },

        link(scope: ng.IScope, element: ng.IAugmentedJQuery) {
            let reactFn: Function;
            let reactProps: any;

            let rootElement: HTMLElement;

            scope.$on("$destroy", () => setRootElement(undefined));

            scope.$watch("omReact", (v: any) => {
                reactFn = v?.component;
                reactProps = v?.props ?? {};

                const update: (run: () => void) => void = reactProps.update;

                // This adds an "update" function to all props passed to React
                // This function must be called by the React component to force a "digest"
                // If the initial props already included one, then it is stil called
                // All "update" function also accept a function which, if defined, will be called initially

                reactProps.update = (run: () => void) => {
                    update && update(run);
                    scope.$applyAsync();
                };

                render();
            });

            function setRootElement(newRootElement: HTMLElement) {
                if (rootElement && newRootElement !== rootElement) {
                    ReactDOM.unmountComponentAtNode(rootElement);
                }

                rootElement = newRootElement;
            }

            function render() {
                setRootElement(element[0]);
                const reactElt = React.createElement(reactFn as unknown as any, reactProps);

                ReactDOM.render(reactElt, rootElement);
            }
        },
    };
}
